import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData } from '@tanstack/react-query';
import { Select, Space, Spin, Row, Col, Tag, Input } from 'antd';
import { useWisecureQuery } from '@wisecure/api-client/hooks';
import { queryKeys } from '@/api/query-keys';
import { PageHeader } from '@/components/PageHeader';
import { SectorMap } from '@/components/SectorMap';
import { SectorCard } from '@/components/SectorCard';
import type { PaginatedResponse } from '@/types/dashboard';

interface SectorListItem {
  id: string;
  label: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  rangeKm: number;
  type: string;
  societyId: string;
  society?: { id: string; name: string; type: string; logo?: string; city?: string };
}

interface SectorMapItemClean {
  id: string;
  label: string;
  address?: string;
  latitude: number;
  longitude: number;
  rangeKm: number;
  society: { id: string; name: string; logo?: string; type: string; city?: string };
}

const SOCIETY_TYPE_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'SECURITY', label: 'Sécurité' },
  { value: 'INSTALLER', label: 'Installateur' },
  { value: 'DISTRIBUTOR', label: 'Distributeur' },
];

function cleanSector(s: SectorListItem): SectorMapItemClean {
  return {
    id: s.id,
    label: s.label,
    address: s.address,
    latitude: typeof s.latitude === 'number' ? s.latitude : 0,
    longitude: typeof s.longitude === 'number' ? s.longitude : 0,
    rangeKm: s.rangeKm,
    society: {
      id: s.society?.id ?? s.societyId,
      name: s.society?.name ?? '',
      logo: s.society?.logo,
      type: s.society?.type ?? s.type,
      city: s.society?.city,
    },
  };
}

export function SectorsPage() {
  const navigate = useNavigate();
  const [selectedSectorId, setSelectedSectorId] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | undefined>();

  const { data: sectorsResponse, isLoading, isFetching } = useWisecureQuery<PaginatedResponse<SectorListItem>>(
    [...queryKeys.dashboardSectorMap, { search, city: cityFilter, type: typeFilter }],
    (client) =>
      client.sectors.sectorsControllerFindAll({
        limit: 200,
        ...(search ? { search } : {}),
        ...(cityFilter ? { city: cityFilter } : {}),
        ...(typeFilter ? { type: typeFilter } : {}),
      }) as Promise<PaginatedResponse<SectorListItem>>,
    { placeholderData: keepPreviousData },
  );

  const sectors: SectorMapItemClean[] = useMemo(() => {
    if (!sectorsResponse?.data) return [];
    return sectorsResponse.data.map(cleanSector);
  }, [sectorsResponse]);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Secteurs"
        subtitle={`${sectors.length} secteur(s) affiché(s)`}
        extra={isFetching ? <Spin size="small" /> : undefined}
      />

      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          placeholder="Rechercher par label, adresse ou entreprise..."
          allowClear
          onSearch={(value) => setSearch(value)}
          onChange={(e) => { if (!e.target.value) setSearch(''); }}
          style={{ width: 300 }}
        />
        <Input.Search
          placeholder="Filtrer par ville..."
          allowClear
          onSearch={(value) => setCityFilter(value)}
          onChange={(e) => { if (!e.target.value) setCityFilter(''); }}
          style={{ width: 200 }}
        />
        <Select
          value={typeFilter}
          onChange={setTypeFilter}
          options={SOCIETY_TYPE_OPTIONS}
          style={{ width: 200 }}
        />
      </Space>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Tag color="green">Sécurité</Tag>
          <Tag color="blue">Installateur</Tag>
          <Tag color="orange">Distributeur</Tag>
        </Space>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <SectorMap
            sectors={sectors}
            selectedSectorId={selectedSectorId}
            onSelectSector={setSelectedSectorId}
            center={mapCenter}
          />
        </Col>
        <Col xs={24} lg={8}>
          <div style={{ maxHeight: 500, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sectors.length === 0 && !isFetching && (
              <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                Aucun secteur trouvé.
              </div>
            )}
            {sectors.map((sector) => (
              <SectorCard
                key={sector.id}
                id={sector.id}
                label={sector.label}
                address={sector.address}
                rangeKm={sector.rangeKm}
                societyName={sector.society.name}
                societyType={sector.society.type}
                isSelected={sector.id === selectedSectorId}
                onSelect={() => setSelectedSectorId(sector.id)}
                onCenterMap={() => setMapCenter({ lat: sector.latitude, lng: sector.longitude })}
                onViewSociety={() => navigate(`/societies/${sector.society.id}`)}
              />
            ))}
          </div>
        </Col>
      </Row>
    </div>
  );
}
